import React, { useRef, useState, useCallback } from 'react';
import { Move } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';

const pairs = [
  {
    id: 'kitchen',
    labelKey: 'ba.kitchen',
    before: 'https://images.pexels.com/photos/3787027/pexels-photo-3787027.jpeg?auto=compress&cs=tinysrgb&w=1400',
    after: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=1400&q=70',
  },
  {
    id: 'bedroom',
    labelKey: 'ba.bedroom',
    before: 'https://images.pexels.com/photos/18063446/pexels-photo-18063446.jpeg?auto=compress&cs=tinysrgb&w=1400',
    after: 'https://images.pexels.com/photos/7033676/pexels-photo-7033676.jpeg?auto=compress&cs=tinysrgb&w=1400',
  },
  {
    id: 'bathroom',
    labelKey: 'ba.bathroom',
    before: 'https://images.unsplash.com/photo-1414452110837-9dab484a417d?w=1400&q=70',
    after: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1400&q=70',
  },
];

function BASlider({ before, after }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setPct = useCallback((clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onMove = (e) => {
    if (!dragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setPct(clientX);
  };

  const stop = () => { dragging.current = false; };

  const start = (e) => {
    dragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setPct(clientX);
  };

  return (
    <div
      ref={ref}
      className="ba-container"
      onMouseDown={start}
      onMouseMove={onMove}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={start}
      onTouchMove={onMove}
      onTouchEnd={stop}
    >
      <img src={before} alt="Before" className="ba-img" draggable={false} />
      <div className="ba-after-wrap" style={{ width: `${pos}%` }}>
        <img src={after} alt="After" className="ba-img" style={{ width: `${100 / (pos / 100 || 0.0001)}%`, maxWidth: 'none' }} draggable={false} />
      </div>
      <span className="ba-label before">Before</span>
      <span className="ba-label after">After</span>
      <div className="ba-handle" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
        <span className="ba-handle-dot">
          <Move size={18} />
        </span>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const { lang } = useT();
  const [active, setActive] = useState(0);

  const labels = {
    kitchen: lang === 'pt' ? 'Cozinha' : 'Kitchen',
    bedroom: lang === 'pt' ? 'Quarto' : 'Bedroom',
    bathroom: lang === 'pt' ? 'Banheiro' : 'Bathroom',
  };

  const kicker = lang === 'pt' ? 'Resultados Reais' : 'Real Results';
  const heading1 = lang === 'pt' ? 'O antes' : 'The before';
  const heading2 = lang === 'pt' ? 'e o depois' : 'and the after';
  const sub = lang === 'pt'
    ? 'Arraste a alça no meio da imagem para revelar a transformação completa.'
    : 'Drag the handle in the middle of the image to reveal the full transformation.';

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <p className="uc-kicker mb-3">{kicker}</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1a2851]">
            {heading1} <span className="text-[#4dbfae]">{heading2}</span>
          </h2>
          <p className="text-[#5b6469] mt-4 max-w-2xl mx-auto">{sub}</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {pairs.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setActive(i)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold border-2 transition-colors ${
                active === i
                  ? 'border-[#4dbfae] bg-[#4dbfae] text-white'
                  : 'border-[#eef6f4] text-[#1a2851] hover:border-[#4dbfae]/50'
              }`}
            >
              {labels[p.id]}
            </button>
          ))}
        </div>

        <BASlider before={pairs[active].before} after={pairs[active].after} />
      </div>
    </section>
  );
}
